import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, LogOut, Search } from 'lucide-react';

import { VendorState } from '../../redux/vendor/rootReducer';
import { toggleDrawer } from '../../redux/vendor/ui/uiActions';
import ardealyLogo from '../../assets/ardealy-mark.svg';
import { AuroraWash } from '../../components/AuroraWash';
import { VendorMenu } from '../../modules/vendor/side-bar/vendor.sidebar-menu';
import { WorkspaceSwitcher } from '../../modules/vendor/side-bar/WorkspaceSwitcher';
import { useWorkspaceFollowsRoute, useWorkspaceStore } from '../../modules/vendor/side-bar/workspace.store';
import { effectiveWorkspace, workspaceById } from '../../modules/vendor/side-bar/workspaces';
import { SuperAdminMenu } from '../../modules/superadmin/side-bar/super-admin.sidebar-menu';
import { useAuthContext } from '../hooks/use-auth-context.hook';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SidebarCredits } from './SidebarCredits';
import { openCommandPalette } from '../../modules/vendor/command-palette/CommandPalette';
import { displayNameFor, initialsFor } from '../utils/user-display';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * The application sidebar.
 *
 * Structure only: shadcn's sidebar owns the chrome, the collapse behaviour
 * and the icon-only mode, so what is written here is the brand at the top,
 * whichever menu the signed-in role gets, and the account at the bottom.
 */
export function SideBar() {
  const { isSuperAdmin, user, logout } = useAuthContext();
  const { isDrawerOpen } = useSelector((state: VendorState) => state.ui);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { state } = useSidebar();
  const workspace = useWorkspaceStore((s) => s.workspace);
  useWorkspaceFollowsRoute();
  // Super admins have one menu and no workspaces; their rail keeps the artwork as drawn.
  const hue = isSuperAdmin ? 0 : workspaceById(effectiveWorkspace(workspace, (user as any)?.vendor?.kind)).hue;

  useEffect(() => {
    dispatch(toggleDrawer(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logOut = async () => {
    try {
      logout();
      navigate('/');
    } catch {
      /* the provider has already cleared what it can */
    }
  };

  const fullName = user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : '';
  const userName = displayNameFor(fullName, user?.email);
  const initials = initialsFor(fullName, user?.email);

  return (
    /*
     * The side follows the reading direction. The rail's placeholder is in
     * the page's flex row, so in Arabic it moved to the right on its own while
     * the fixed panel stayed pinned left — covering the start of every page.
     */
    <Sidebar collapsible="icon" variant="inset" side={i18n.dir() === 'rtl' ? 'right' : 'left'}>
      <AuroraWash className="inset-x-0" compact={state === 'collapsed'} hue={hue} />
      {/* `items-center` only when collapsed: the mark then sits on the same
          vertical axis as every icon below it (24px into a 48px rail), where
          left-aligned at px-2 it sat 6px shy of them. Expanded, it keeps the
          px-6 alignment it shares with the search field. */}
      <SidebarHeader className="h-16 flex-row items-center gap-3 px-6 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2">
        {/* The mark alone — black on light, reversed on dark. `brightness-0`
            flattens whatever colour the SVG carries. */}
        {/* The mark goes Home, the one workspace that looks across the others. */}
        <button
          type="button"
          onClick={() => {
            if (!isSuperAdmin) useWorkspaceStore.getState().setWorkspace('home');
            navigate(isSuperAdmin ? '/' : '/home/dashboard');
          }}
          aria-label={t('WORKSPACE.HOME', 'Home') as string}
          className="relative z-10 shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img src={ardealyLogo} alt="ardealy" className="size-5 brightness-0 dark:invert" />
        </button>
        {/* Beside the mark, where the product's name would sit: which part of
            the backoffice this is. Hidden on the icon rail, which has no room
            for a name — expanding the rail brings it back. */}
        {!isSuperAdmin && (
          <div className="ms-auto flex min-w-0 group-data-[collapsible=icon]:hidden">
            <WorkspaceSwitcher />
          </div>
        )}
      </SidebarHeader>

      {/*
        The search field belongs to the sidebar, not the bar. It is a button
        dressed as an input: typing happens in the command palette, which
        already knows every destination, product and how to ask the assistant.
      */}
      <div className="px-6 pb-5 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:pb-2">
        <button
          type="button"
          onClick={openCommandPalette}
          aria-label={t('TOPBAR.SEARCH_ANYTHING') as string}
          className="relative z-10 flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-white px-2.5 text-sm text-muted-foreground shadow-sm transition-colors hover:bg-white dark:bg-input dark:hover:bg-input group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        >
          <Search className="size-4 shrink-0" />
          <span className="flex-1 truncate text-start whitespace-nowrap group-data-[collapsible=icon]:hidden">
            {t('TOPBAR.SEARCH_ANYTHING')}
          </span>
          {/* ⌘ draws tall and K draws small at one size, so K is set larger to
              make the pair read evenly. */}
          <kbd className="pointer-events-none flex items-center gap-px rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-muted-foreground select-none group-data-[collapsible=icon]:hidden">
            <span>⌘</span>
            <span className="text-[13px]">K</span>
          </kbd>
        </button>
      </div>

      <SidebarContent className="gap-0">
        {isSuperAdmin ? <SuperAdminMenu isDrawerOpen={isDrawerOpen} isCollapsed={false} /> : <VendorMenu />}
      </SidebarContent>

      {/*
        Outside SidebarContent, which is the only thing here that scrolls
        (`flex-1 overflow-auto`). Being a sibling of it rather than a child is
        what pins this to the bottom of the rail while a long menu runs past
        underneath — no sticky positioning, no z-index.

        Above the separator, so the rule reads as the line between the account
        and everything else rather than between two footer rows.
      */}
      {!isSuperAdmin && (
        <div className="relative bg-sidebar px-2 pb-2 pt-1">
          {/*
            A short fade of the rail's own colour over the last pixels of the
            scroll area, so a long menu dissolves under this block instead of
            ending in a hard cut against it. Negative offset to overlap the
            scroll area: later siblings paint on top, so no z-index.
          */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -top-4 h-4 bg-gradient-to-b from-transparent to-sidebar"
          />
          <SidebarCredits />
        </div>
      )}

      <SidebarSeparator className="mx-0" />

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            {/*
              A menu, not a button. The whole row used to log the vendor out
              on a single stray click, with nothing asked and nothing undone.
            */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* No `tooltip` here: it wraps the button in a Tooltip, which
                    breaks DropdownMenuTrigger's asChild chain and the menu
                    never opens. The row shows the name anyway. */}
                <SidebarMenuButton size="lg" className="h-12">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary text-[11px] text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-start leading-tight">
                    <span className="truncate text-sm font-medium text-foreground">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ms-auto size-4 text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>

              {/* Opens upward from the footer: anchored to the right of a
                  fixed sidebar, Radix measured the trigger wrongly and put the
                  menu above the viewport. */}
              <DropdownMenuContent
                side="top"
                align="start"
                sideOffset={8}
                collisionPadding={8}
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
              >
                <DropdownMenuLabel className="font-normal">
                  <span className="block truncate text-sm font-medium">{userName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logOut} variant="destructive">
                  <LogOut className="size-4" />
                  {t('SIDEBAR.LOGOUT')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

    </Sidebar>
  );
}
